import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormErrorMessage,
	Heading,
	HStack,
	Input,
	Link,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Stack,
	Text,
	useDisclosure
} from "@chakra-ui/react";
import {AdvancedMarker, APIProvider, Map, MapMouseEvent} from "@vis.gl/react-google-maps";
import React, {useEffect, useMemo, useState} from "react";
import HelpTooltip from "../../help/HelpTooltip";
import {CircleSpot, LatLong, MarkerSpot, PolygonSpot, Spot, SpotType} from "../../types";

const GOOGLE_MAPS_API_KEY = "AIzaSyCcdolz5IqPuQZ5MuZdXEHVgdoTukvbblE";
const PICKER_MAP_ID = "SOUNDSPOTS_VENUE_PICKER";
const DEFAULT_FALLBACK_CENTER: LatLong = {lat: 56.0573, lng: 10.2028};

type VisibilitySettingsTabProps = {
	canEdit: boolean;
	isPublic: boolean;
	publicAccessCode: string;
	publicAccessCodeError?: string;
	position: LatLong | undefined;
	spots: Spot[];
	onPublicChange: (value: boolean) => void;
	onPublicAccessCodeChange: (value: string) => void;
	onPositionChange: (value: LatLong | undefined) => void;
};

const computeSpotsCentroid = (spots: Spot[]): LatLong | undefined => {
	const points: LatLong[] = [];
	for (const spot of spots) {
		if (spot.type === SpotType.Marker) {
			const marker = spot as MarkerSpot;
			if (marker.position && Number.isFinite(marker.position.lat) && Number.isFinite(marker.position.lng)) {
				points.push({lat: marker.position.lat, lng: marker.position.lng});
			}
		} else if (spot.type === SpotType.Circle) {
			const circle = spot as CircleSpot;
			if (circle.center && Number.isFinite(circle.center.lat) && Number.isFinite(circle.center.lng)) {
				points.push({lat: circle.center.lat, lng: circle.center.lng});
			}
		} else if (spot.type === SpotType.Polygon) {
			const polygon = spot as PolygonSpot;
			const coords = Array.isArray(polygon.coords) ? polygon.coords : [];
			if (coords.length === 0) {
				continue;
			}
			const latSum = coords.reduce((sum, c) => sum + (Number.isFinite(c.lat) ? c.lat : 0), 0);
			const lngSum = coords.reduce((sum, c) => sum + (Number.isFinite(c.lng) ? c.lng : 0), 0);
			points.push({lat: latSum / coords.length, lng: lngSum / coords.length});
		}
	}
	if (points.length === 0) {
		return undefined;
	}
	const lat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
	const lng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
	return {lat, lng};
};

const formatCoord = (value: number | undefined): string => {
	if (value === undefined || !Number.isFinite(value)) {
		return "—";
	}
	return value.toFixed(5);
};

type LocationPickerDialogProps = {
	isOpen: boolean;
	onClose: () => void;
	initialPosition: LatLong | undefined;
	fallbackCenter: LatLong;
	shouldRequestUserLocation: boolean;
	onSubmit: (position: LatLong) => void;
};

const LocationPickerDialog = ({isOpen, onClose, initialPosition, fallbackCenter, shouldRequestUserLocation, onSubmit}: LocationPickerDialogProps) => {
	const [pendingPosition, setPendingPosition] = useState<LatLong | undefined>(initialPosition);
	const [mapCenter, setMapCenter] = useState<LatLong>(initialPosition || fallbackCenter);
	const [mapZoom, setMapZoom] = useState<number>(initialPosition ? 15 : 10);
	const [isLocating, setIsLocating] = useState<boolean>(false);
	const [locationError, setLocationError] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		setPendingPosition(initialPosition);
		setMapCenter(initialPosition || fallbackCenter);
		setMapZoom(initialPosition ? 15 : 10);
		setLocationError(undefined);
		if (initialPosition || !shouldRequestUserLocation) {
			setIsLocating(false);
			return;
		}
		if (typeof navigator === "undefined" || !navigator.geolocation) {
			setIsLocating(false);
			return;
		}
		setIsLocating(true);
		let cancelled = false;
		navigator.geolocation.getCurrentPosition(
			(geo) => {
				if (cancelled) {
					return;
				}
				const next = {lat: geo.coords.latitude, lng: geo.coords.longitude};
				setPendingPosition(next);
				setMapCenter(next);
				setMapZoom(14);
				setIsLocating(false);
			},
			(error) => {
				if (cancelled) {
					return;
				}
				setLocationError(error.message || "Couldn't access your location.");
				setIsLocating(false);
			},
			{enableHighAccuracy: false, timeout: 10000, maximumAge: 60000}
		);
		return () => {
			cancelled = true;
		};
	}, [isOpen, initialPosition?.lat, initialPosition?.lng, shouldRequestUserLocation, fallbackCenter.lat, fallbackCenter.lng]);

	const handleMapClick = (event: MapMouseEvent) => {
		const latLng = event.detail.latLng;
		if (!latLng) {
			return;
		}
		setPendingPosition({lat: latLng.lat, lng: latLng.lng});
	};

	const handleSubmit = () => {
		if (pendingPosition) {
			onSubmit(pendingPosition);
			onClose();
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size={"4xl"}>
			<ModalOverlay/>
			<ModalContent>
				<ModalHeader>Pick venue location</ModalHeader>
				<ModalCloseButton/>
				<ModalBody>
					<Stack spacing={3}>
						<Text fontSize={"sm"} color={"gray.600"}>
							Click on the map to place a pin for the venue. You can click again to move it.
						</Text>
						<Box height={"460px"} borderRadius={"md"} overflow={"hidden"} border={"1px solid"} borderColor={"gray.200"}>
							<APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
								<Map
									mapId={PICKER_MAP_ID}
									center={mapCenter}
									zoom={mapZoom}
									style={{width: "100%", height: "100%"}}
									gestureHandling={"greedy"}
									disableDoubleClickZoom={false}
									streetViewControl={false}
									fullscreenControl={false}
									onClick={handleMapClick}
									onCameraChanged={(event) => {
										setMapCenter(event.detail.center);
										setMapZoom(event.detail.zoom);
									}}
								>
									{pendingPosition ? (
										<AdvancedMarker position={pendingPosition}/>
									) : null}
								</Map>
							</APIProvider>
						</Box>
						{isLocating ? (
							<Text fontSize={"sm"} color={"gray.600"}>Locating you…</Text>
						) : locationError ? (
							<Text fontSize={"sm"} color={"orange.600"}>{locationError}</Text>
						) : (
							<Text fontSize={"sm"} color={"gray.600"}>
								{pendingPosition
									? `Selected: ${pendingPosition.lat.toFixed(5)}, ${pendingPosition.lng.toFixed(5)}`
									: "No location selected yet."}
							</Text>
						)}
					</Stack>
				</ModalBody>
				<ModalFooter>
					<Button variant={"ghost"} onClick={onClose}>Cancel</Button>
					<Button colorScheme={"blue"} onClick={handleSubmit} isDisabled={!pendingPosition}>
						Use this location
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

const VisibilitySettingsTab = ({
	canEdit,
	isPublic,
	publicAccessCode,
	publicAccessCodeError,
	position,
	spots,
	onPublicChange,
	onPublicAccessCodeChange,
	onPositionChange
}: VisibilitySettingsTabProps) => {
	const {isOpen, onOpen, onClose} = useDisclosure();
	const centroidFromSpots = useMemo(() => computeSpotsCentroid(spots), [spots]);
	const hasValidPosition = !!position && Number.isFinite(position.lat) && Number.isFinite(position.lng);
	const mapsLink = hasValidPosition
		? `https://www.google.com/maps?q=${position!.lat},${position!.lng}`
		: undefined;
	const dialogInitialPosition = hasValidPosition ? position : centroidFromSpots;
	const dialogShouldRequestLocation = !dialogInitialPosition;

	return (
		<Stack spacing={6}>
			<Box>
				<Heading size={"sm"} mb={2}>Visibility</Heading>
				<Stack spacing={3}>
					<Checkbox
						isChecked={isPublic}
						onChange={(event) => onPublicChange(event.target.checked)}
						isDisabled={!canEdit}
					>
						Make publicly available
						<HelpTooltip
							content={"Allow this space to be accessed publicly without requiring an account."}
							placement={"right"}
							icon
							iconAriaLabel={"Publicly available info"}
						/>
					</Checkbox>
				</Stack>
			</Box>
			<Box>
				<Heading size={"sm"} mb={2}>Access code</Heading>
				<FormControl isInvalid={!!publicAccessCodeError}>
					<Stack spacing={2} maxW={"420px"}>
						<Input
							value={publicAccessCode}
							onChange={(event) => onPublicAccessCodeChange(event.target.value)}
							isDisabled={!canEdit}
							placeholder={"Optional 6-digit code"}
							maxW={"240px"}
							inputMode={"numeric"}
							maxLength={6}
						/>
						{publicAccessCodeError ? (
							<FormErrorMessage>{publicAccessCodeError}</FormErrorMessage>
						) : (
							<Text fontSize={"sm"} color={"gray.600"}>
								Optional. Leave empty for no access code.
							</Text>
						)}
					</Stack>
				</FormControl>
			</Box>
			<Box>
				<Heading size={"sm"} mb={1}>Venue location</Heading>
				<Text fontSize={"sm"} color={"gray.600"} mb={3}>
					Pin where this space lives on the map. Used by the Public Spaces page and the app to surface nearby spaces.
				</Text>
				<Stack spacing={3} maxW={"520px"}>
					<HStack spacing={6}>
						<Box>
							<Text fontSize={"xs"} color={"gray.500"} textTransform={"uppercase"} letterSpacing={"0.05em"}>
								Latitude
							</Text>
							<Text fontSize={"md"} fontFamily={"mono"}>
								{formatCoord(position?.lat)}
							</Text>
						</Box>
						<Box>
							<Text fontSize={"xs"} color={"gray.500"} textTransform={"uppercase"} letterSpacing={"0.05em"}>
								Longitude
							</Text>
							<Text fontSize={"md"} fontFamily={"mono"}>
								{formatCoord(position?.lng)}
							</Text>
						</Box>
					</HStack>
					<HStack spacing={3} flexWrap={"wrap"}>
						<Button
							size={"sm"}
							colorScheme={"blue"}
							variant={"outline"}
							onClick={onOpen}
							isDisabled={!canEdit}
						>
							{hasValidPosition ? "Edit on map" : "Pick on map"}
						</Button>
						{hasValidPosition ? (
							<Button
								size={"sm"}
								variant={"ghost"}
								onClick={() => onPositionChange(undefined)}
								isDisabled={!canEdit}
							>
								Clear
							</Button>
						) : null}
						{mapsLink ? (
							<Link href={mapsLink} isExternal fontSize={"sm"} color={"blue.600"}>
								Open in Google Maps
							</Link>
						) : null}
					</HStack>
				</Stack>
			</Box>
			{!canEdit ? (
				<Text fontSize={"sm"} color={"orange.600"}>
					Space settings can only be updated while editing a draft.
				</Text>
			) : null}
			<LocationPickerDialog
				isOpen={isOpen}
				onClose={onClose}
				initialPosition={dialogInitialPosition}
				fallbackCenter={DEFAULT_FALLBACK_CENTER}
				shouldRequestUserLocation={dialogShouldRequestLocation}
				onSubmit={(next) => onPositionChange(next)}
			/>
		</Stack>
	);
};

export default VisibilitySettingsTab;
